import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  Briefcase,
  Calendar,
  Check,
  Coffee,
  Flag,
  FolderPlus,
  GripVertical,
  Heart,
  Home,
  Lightbulb,
  ListTodo,
  Palette,
  Search,
  ShoppingCart,
  Star,
  StickyNote,
  Target,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { Checkbox } from "../components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import ConvertTaskModal from "../components/quicknotes/ConvertTaskModal";

const CAT_COLORS = {
  amber: { text: "text-amber-700", border: "border-amber-100", swatch: "bg-amber-500" },
  red: { text: "text-red-700", border: "border-red-100", swatch: "bg-red-500" },
  orange: { text: "text-orange-700", border: "border-orange-100", swatch: "bg-orange-500" },
  emerald: { text: "text-emerald-700", border: "border-emerald-100", swatch: "bg-emerald-500" },
  sky: { text: "text-sky-700", border: "border-sky-100", swatch: "bg-sky-500" },
  violet: { text: "text-violet-700", border: "border-violet-100", swatch: "bg-violet-500" },
  pink: { text: "text-pink-700", border: "border-pink-100", swatch: "bg-pink-500" },
  zinc: { text: "text-zinc-700", border: "border-zinc-200", swatch: "bg-zinc-500" },
};
const CAT_SIZES = { sm: "text-[11px]", md: "text-sm", lg: "text-lg" };
const CAT_ICONS = [
  ["briefcase", Briefcase],
  ["home", Home],
  ["cart", ShoppingCart],
  ["star", Star],
  ["heart", Heart],
  ["flag", Flag],
  ["idea", Lightbulb],
  ["calendar", Calendar],
  ["book", BookOpen],
  ["coffee", Coffee],
  ["target", Target],
  ["zap", Zap],
];
const ICON_BY_KEY = Object.fromEntries(CAT_ICONS);

/**
 * Hızlı Not — kişisel checklist. Kategori başlıkları altında gruplama,
 * sürükle-bırak sıralama/taşıma, kategori stili (renk/boyut/ikon),
 * silme onayı ve arama.
 */
export default function QuickNotes() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [draft, setDraft] = useState("");
  const [sq, setSq] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [catDraft, setCatDraft] = useState("");
  const [deleteCat, setDeleteCat] = useState(null);
  const [convertNote, setConvertNote] = useState(null);
  const inputRef = useRef(null);
  const notesRef = useRef(notes);
  const catsRef = useRef(categories);
  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);
  useEffect(() => {
    catsRef.current = categories;
  }, [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const load = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([
        api.get("/quick-notes"),
        api.get("/quick-notes/categories"),
      ]);
      setNotes(n.data || []);
      setCategories(c.data || []);
    } catch {
      /* ignore */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    setTimeout(() => inputRef.current?.focus(), 40);
  }, [load]);

  const addNote = async (text, categoryId = null) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return false;
    setSaving(true);
    const tmpId = `tmp-${Date.now()}`;
    setNotes((prev) => [
      ...prev,
      { id: tmpId, text: trimmed, done: false, category_id: categoryId, _pending: true },
    ]);
    try {
      const { data } = await api.post("/quick-notes", {
        text: trimmed,
        category_id: categoryId,
      });
      setNotes((prev) => prev.map((n) => (n.id === tmpId ? data : n)));
      return true;
    } catch (err) {
      setNotes((prev) => prev.filter((n) => n.id !== tmpId));
      toast.error(err?.response?.data?.detail || "Not eklenemedi");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addFromMain = async () => {
    if (await addNote(draft, null)) setDraft("");
    inputRef.current?.focus();
  };

  const toggleDone = async (note) => {
    const nextDone = !note.done;
    setNotes((prev) =>
      prev.map((n) => (n.id === note.id ? { ...n, done: nextDone } : n)),
    );
    try {
      await api.patch(`/quick-notes/${note.id}`, { done: nextDone });
    } catch (err) {
      setNotes((prev) =>
        prev.map((n) => (n.id === note.id ? { ...n, done: !nextDone } : n)),
      );
      toast.error(err?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const updateText = async (note, newText) => {
    const trimmed = (newText || "").trim();
    if (trimmed === note.text) return;
    setNotes((prev) =>
      prev.map((n) => (n.id === note.id ? { ...n, text: trimmed } : n)),
    );
    try {
      await api.patch(`/quick-notes/${note.id}`, { text: trimmed });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Güncellenemedi");
      load();
    }
  };

  const remove = async (note) => {
    setNotes((prev) => prev.filter((n) => n.id !== note.id));
    try {
      await api.delete(`/quick-notes/${note.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
      load();
    }
  };

  const addCategory = async () => {
    const name = catDraft.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/quick-notes/categories", { name });
      setCategories((prev) => [...prev, data]);
      setCatDraft("");
      setAddingCat(false);
      toast.success(`"${data.name}" kategorisi eklendi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kategori eklenemedi");
    }
  };

  const updateCategory = async (cat, patch) => {
    setCategories((prev) =>
      prev.map((c) => (c.id === cat.id ? { ...c, ...patch } : c)),
    );
    try {
      await api.patch(`/quick-notes/categories/${cat.id}`, patch);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
      load();
    }
  };

  const deleteCategory = async (cat) => {
    setCategories((prev) => prev.filter((c) => c.id !== cat.id));
    setNotes((prev) =>
      prev.map((n) => (n.category_id === cat.id ? { ...n, category_id: null } : n)),
    );
    try {
      await api.delete(`/quick-notes/categories/${cat.id}`);
      toast.success(`"${cat.name}" silindi — notları Genel'e taşındı`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
      load();
    }
  };

  // --- Sürükle-bırak ---
  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const aid = String(active.id);
    if (!aid.startsWith("note:")) return;
    const oid = String(over.id);
    const prev = notesRef.current;
    const activeNote = prev.find((n) => n.id === aid.slice(5));
    if (!activeNote) return;
    let target;
    if (oid.startsWith("note:")) {
      const overNote = prev.find((n) => n.id === oid.slice(5));
      if (!overNote) return;
      target = overNote.category_id || null;
    } else if (oid.startsWith("cat:")) {
      target = oid.slice(4);
    } else if (oid === "general-drop") {
      target = null;
    } else return;
    if ((activeNote.category_id || null) === target) return;
    const without = prev.filter((n) => n.id !== activeNote.id);
    const moved = { ...activeNote, category_id: target };
    let next;
    if (oid.startsWith("note:")) {
      const idx = without.findIndex((n) => n.id === oid.slice(5));
      next = [...without.slice(0, idx), moved, ...without.slice(idx)];
    } else {
      next = [...without, moved];
    }
    setNotes(next);
  };

  const onDragEnd = ({ active, over }) => {
    const aid = String(active.id);
    const oid = over ? String(over.id) : null;
    if (aid.startsWith("cat:")) {
      if (!oid) return;
      let targetCatId = null;
      if (oid.startsWith("cat:")) targetCatId = oid.slice(4);
      else if (oid.startsWith("note:")) {
        const overNote = notesRef.current.find((n) => n.id === oid.slice(5));
        targetCatId = overNote?.category_id || null;
      }
      if (!targetCatId || targetCatId === aid.slice(4)) return;
      const prev = catsRef.current;
      const from = prev.findIndex((c) => c.id === aid.slice(4));
      const to = prev.findIndex((c) => c.id === targetCatId);
      if (from < 0 || to < 0 || from === to) return;
      const next = arrayMove(prev, from, to);
      setCategories(next);
      api
        .put("/quick-notes/categories/reorder", { ids: next.map((c) => c.id) })
        .catch(() => {
          toast.error("Sıralama kaydedilemedi");
          load();
        });
      return;
    }
    if (!aid.startsWith("note:")) return;
    const noteId = aid.slice(5);
    let next = notesRef.current;
    const activeNote = next.find((n) => n.id === noteId);
    if (!activeNote) return;
    if (oid && oid.startsWith("note:") && oid !== aid) {
      const overNote = next.find((n) => n.id === oid.slice(5));
      if (
        overNote &&
        (overNote.category_id || null) === (activeNote.category_id || null)
      ) {
        const from = next.findIndex((n) => n.id === noteId);
        const to = next.findIndex((n) => n.id === oid.slice(5));
        next = arrayMove(next, from, to);
        setNotes(next);
      }
    }
    const cid = activeNote.category_id || null;
    const ids = next
      .filter((n) => (n.category_id || null) === cid)
      .map((n) => n.id);
    api.put("/quick-notes/reorder", { category_id: cid, ids }).catch(() => {
      toast.error("Sıralama kaydedilemedi");
      load();
    });
  };

  // --- Arama + gruplama ---
  const sqNorm = sq.trim().toLocaleLowerCase("tr");
  const searching = sqNorm.length > 0;
  const matches = (n) =>
    !searching || (n.text || "").toLocaleLowerCase("tr").includes(sqNorm);

  const catIds = useMemo(() => new Set(categories.map((c) => c.id)), [categories]);
  const general = notes.filter(
    (n) => (!n.category_id || !catIds.has(n.category_id)) && matches(n),
  );
  const visibleCats = categories.filter((cat) => {
    if (!searching) return true;
    if (cat.name.toLocaleLowerCase("tr").includes(sqNorm)) return true;
    return notes.some((n) => n.category_id === cat.id && matches(n));
  });
  const noResults =
    searching && general.length === 0 && visibleCats.length === 0;

  if (!user) return null;

  const total = notes.length;
  const openCount = notes.filter((n) => !n.done).length;

  return (
    <div className="space-y-8" data-testid="quick-notes-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            {t("brand")}
          </div>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            <StickyNote className="h-7 w-7 text-amber-500" strokeWidth={1.75} />
            {t("nav.quick_note")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("quick_notes.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm text-zinc-600">
          <span
            data-testid="quick-notes-open-count"
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200"
          >
            {openCount} {t("quick_notes.open")}
          </span>
          <span className="text-xs text-zinc-400">
            {total} {t("quick_notes.total")}
          </span>
        </div>
      </div>

      <div className="max-w-3xl rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              addFromMain();
            }
          }}
          disabled={saving}
          placeholder={t("quick_notes.placeholder")}
          data-testid="quick-notes-input"
          className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200"
        />
        <p className="mt-2 text-[11px] text-zinc-400">
          {t("quick_notes.hint")}
        </p>

        <div className="relative mt-3">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={sq}
            onChange={(e) => setSq(e.target.value)}
            placeholder="Notlarda ara…"
            data-testid="quick-notes-search-input"
            className="w-full rounded-md border border-zinc-200 bg-zinc-50/60 py-1.5 pl-8 pr-8 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-200"
          />
          {searching && (
            <button
              type="button"
              onClick={() => setSq("")}
              data-testid="quick-notes-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-zinc-400 hover:text-zinc-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
        >
          <div className="mt-4 space-y-1">
            {loaded && total === 0 && categories.length === 0 ? (
              <p className="py-8 text-center text-sm text-zinc-400">
                {t("quick_notes.empty")}
              </p>
            ) : noResults ? (
              <p
                className="py-8 text-center text-sm text-zinc-400"
                data-testid="quick-notes-no-results"
              >
                "{sq.trim()}" için sonuç bulunamadı
              </p>
            ) : (
              <>
                {categories.length > 0 && (general.length > 0 || !searching) && (
                  <GeneralLabel />
                )}
                <SortableContext
                  items={general.map((n) => `note:${n.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {general.map((n) => (
                    <SortableNoteRow
                      key={n.id}
                      note={n}
                      disabled={searching}
                      onToggle={() => toggleDone(n)}
                      onEdit={(v) => updateText(n, v)}
                      onDelete={() => remove(n)}
                      onConvert={() => setConvertNote(n)}
                    />
                  ))}
                </SortableContext>
              </>
            )}
          </div>

          <SortableContext
            items={visibleCats.map((c) => `cat:${c.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {visibleCats.map((cat) => (
              <CategorySection
                key={cat.id}
                category={cat}
                notes={notes.filter((n) => n.category_id === cat.id && matches(n))}
                searching={searching}
                onAddNote={(text) => addNote(text, cat.id)}
                onRename={(name) => updateCategory(cat, { name })}
                onStyleChange={(patch) => updateCategory(cat, patch)}
                onRequestDelete={() => setDeleteCat(cat)}
                onToggle={toggleDone}
                onEdit={updateText}
                onDeleteNote={remove}
                onConvertNote={setConvertNote}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div className="mt-5 border-t border-zinc-100 pt-3">
          {addingCat ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={catDraft}
                onChange={(e) => setCatDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCategory();
                  } else if (e.key === "Escape") {
                    setAddingCat(false);
                    setCatDraft("");
                  }
                }}
                placeholder="Kategori adı… (ör. İş, Kişisel, Alışveriş)"
                data-testid="add-category-input"
                className="flex-1 rounded-md border border-amber-300 bg-amber-50/50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
              <button
                type="button"
                onClick={addCategory}
                data-testid="add-category-confirm"
                className="rounded-md bg-amber-500 p-2 text-white hover:bg-amber-600"
                title="Ekle"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddingCat(false);
                  setCatDraft("");
                }}
                className="rounded-md p-2 text-zinc-400 hover:bg-zinc-100"
                title="Vazgeç"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingCat(true)}
              data-testid="add-category-btn"
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-zinc-500 transition-colors hover:bg-amber-50 hover:text-amber-700"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              Kategori ekle
            </button>
          )}
        </div>
      </div>

      {convertNote && (
        <ConvertTaskModal
          note={convertNote}
          onClose={() => setConvertNote(null)}
          onConverted={(id) =>
            setNotes((prev) => prev.filter((n) => n.id !== id))
          }
        />
      )}

      <AlertDialog
        open={!!deleteCat}
        onOpenChange={(o) => {
          if (!o) setDeleteCat(null);
        }}
      >
        <AlertDialogContent data-testid="cat-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi silmek istediğine emin misin?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteCat?.name}" kategorisi silinecek. İçindeki notlar
              silinmez, Genel bölümüne taşınır.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cat-delete-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cat-delete-confirm"
              onClick={() => {
                if (deleteCat) deleteCategory(deleteCat);
                setDeleteCat(null);
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Evet, sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function GeneralLabel() {
  const { setNodeRef, isOver } = useDroppable({ id: "general-drop" });
  return (
    <div
      ref={setNodeRef}
      data-testid="qn-general-label"
      className={[
        "rounded px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider transition-colors",
        isOver ? "bg-amber-50 text-amber-600" : "text-zinc-400",
      ].join(" ")}
    >
      Genel
    </div>
  );
}

function CategorySection({
  category,
  notes,
  searching,
  onAddNote,
  onRename,
  onStyleChange,
  onRequestDelete,
  onToggle,
  onEdit,
  onDeleteNote,
  onConvertNote,
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(category.name);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `cat:${category.id}`, disabled: searching });

  useEffect(() => setName(category.name), [category.name]);

  const commitRename = () => {
    setRenaming(false);
    onRename(name.trim() || category.name);
  };

  const submitNote = async () => {
    if (await onAddNote(noteDraft)) setNoteDraft("");
  };

  const showInput = !searching && (hovered || focused || noteDraft.trim().length > 0);
  const color = CAT_COLORS[category.color] || CAT_COLORS.amber;
  const sizeCls = CAT_SIZES[category.size] || CAT_SIZES.sm;
  const Icon = ICON_BY_KEY[category.icon];

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={["mt-4", isDragging ? "z-10 opacity-60" : ""].join(" ")}
      data-testid={`qn-category-${category.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`group flex items-center gap-2 border-b px-2 pb-1 ${color.border}`}
      >
        {!searching && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            data-testid={`qn-category-drag-${category.id}`}
            className="hidden shrink-0 cursor-grab touch-none rounded p-0.5 text-zinc-300 hover:text-zinc-500 active:cursor-grabbing group-hover:block"
            aria-label="Kategoriyi sürükle"
            title="Sürükleyerek sırala"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        {Icon && (
          <Icon
            className={`h-3.5 w-3.5 shrink-0 ${color.text}`}
            data-testid={`qn-category-icon-${category.id}`}
          />
        )}
        {renaming ? (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              else if (e.key === "Escape") {
                setName(category.name);
                setRenaming(false);
              }
            }}
            data-testid={`qn-category-rename-${category.id}`}
            className="flex-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider focus:outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => setRenaming(true)}
            data-testid={`qn-category-name-${category.id}`}
            title="Yeniden adlandırmak için tıkla"
            className={`truncate font-semibold uppercase tracking-wider ${color.text} ${sizeCls}`}
          >
            {category.name}
          </button>
        )}
        <span className="text-[10px] text-zinc-400">{notes.length}</span>
        <span className="flex-1" />
        <CategoryStylePopover category={category} onChange={onStyleChange} />
        <button
          type="button"
          onClick={onRequestDelete}
          data-testid={`qn-category-delete-${category.id}`}
          className="hidden rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 group-hover:block"
          title="Kategoriyi sil (notlar Genel'e taşınır)"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-1 space-y-1">
        <SortableContext
          items={notes.map((n) => `note:${n.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {notes.map((n) => (
            <SortableNoteRow
              key={n.id}
              note={n}
              disabled={searching}
              onToggle={() => onToggle(n)}
              onEdit={(v) => onEdit(n, v)}
              onDelete={() => onDeleteNote(n)}
              onConvert={() => onConvertNote(n)}
            />
          ))}
        </SortableContext>
      </div>

      {showInput ? (
        <input
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitNote();
            } else if (e.key === "Escape") {
              setNoteDraft("");
              setFocused(false);
              e.target.blur();
            }
          }}
          placeholder={`${category.name} altına not yaz ve Enter'a bas…`}
          data-testid={`qn-category-note-input-${category.id}`}
          className="mt-1 w-full rounded-md border border-zinc-200 bg-zinc-50/60 px-3 py-1.5 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-200"
        />
      ) : (
        !searching &&
        notes.length === 0 && (
          <p className="px-2 py-1.5 text-xs italic text-zinc-300">
            Not eklemek için üzerine gel
          </p>
        )
      )}
    </div>
  );
}

function CategoryStylePopover({ category, onChange }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`qn-category-style-${category.id}`}
          className="hidden rounded p-1 text-zinc-400 hover:bg-amber-50 hover:text-amber-600 group-hover:block"
          title="Görünümü özelleştir"
        >
          <Palette className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          İkon
        </div>
        <div className="grid grid-cols-7 gap-1">
          <button
            type="button"
            onClick={() => onChange({ icon: "" })}
            data-testid="qn-icon-none"
            title="İkonsuz"
            className={[
              "flex h-7 w-7 items-center justify-center rounded",
              !category.icon
                ? "bg-amber-100 text-amber-700"
                : "text-zinc-400 hover:bg-zinc-100",
            ].join(" ")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
          {CAT_ICONS.map(([key, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => onChange({ icon: key })}
              data-testid={`qn-icon-${key}`}
              className={[
                "flex h-7 w-7 items-center justify-center rounded",
                category.icon === key
                  ? "bg-amber-100 text-amber-700"
                  : "text-zinc-500 hover:bg-zinc-100",
              ].join(" ")}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <div className="mb-1.5 mt-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Renk
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(CAT_COLORS).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onChange({ color: k })}
              data-testid={`qn-color-${k}`}
              title={k}
              className={[
                "h-5 w-5 rounded-full",
                CAT_COLORS[k].swatch,
                (category.color || "amber") === k
                  ? "ring-2 ring-zinc-900 ring-offset-1"
                  : "",
              ].join(" ")}
            />
          ))}
        </div>
        <div className="mb-1.5 mt-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Boyut
        </div>
        <div className="flex gap-1">
          {[
            ["sm", "Küçük"],
            ["md", "Orta"],
            ["lg", "Büyük"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => onChange({ size: k })}
              data-testid={`qn-size-${k}`}
              className={[
                "flex-1 rounded-md border px-2 py-1 text-xs transition-colors",
                (category.size || "sm") === k
                  ? "border-amber-400 bg-amber-50 font-medium text-amber-700"
                  : "border-zinc-200 text-zinc-600 hover:bg-zinc-50",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SortableNoteRow({ note, disabled, onToggle, onEdit, onDelete, onConvert }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `note:${note.id}`, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "z-10 opacity-60" : ""}
    >
      <QuickNoteRow
        note={note}
        onToggle={onToggle}
        onEdit={onEdit}
        onDelete={onDelete}
        onConvert={onConvert}
        dragHandle={
          disabled ? null : (
            <button
              type="button"
              {...attributes}
              {...listeners}
              data-testid={`quick-note-drag-${note.id}`}
              className="hidden shrink-0 cursor-grab touch-none rounded p-0.5 text-zinc-300 hover:text-zinc-500 active:cursor-grabbing group-hover:block"
              aria-label="Notu sürükle"
              title="Sürükleyerek taşı / sırala"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )
        }
      />
    </div>
  );
}

function QuickNoteRow({ note, onToggle, onEdit, onDelete, onConvert, dragHandle }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note.text);

  useEffect(() => {
    setValue(note.text);
  }, [note.text]);

  const commit = () => {
    setEditing(false);
    onEdit(value);
  };

  return (
    <div
      data-testid={`quick-note-${note.id}`}
      className={[
        "group flex items-start gap-2 rounded-md px-2 py-2 hover:bg-zinc-50 transition-colors",
        note.done ? "opacity-70" : "",
      ].join(" ")}
    >
      {dragHandle}
      <Checkbox
        checked={!!note.done}
        onCheckedChange={onToggle}
        data-testid={`quick-note-checkbox-${note.id}`}
        className="mt-0.5"
      />
      {editing ? (
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              setValue(note.text);
              setEditing(false);
            }
          }}
          className="flex-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-sm focus:outline-none"
          data-testid={`quick-note-edit-${note.id}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={[
            "min-w-0 flex-1 text-left text-sm",
            note.done ? "text-zinc-500 line-through" : "text-zinc-800",
          ].join(" ")}
          title="Düzenlemek için tıkla"
        >
          {note.text || <span className="italic text-zinc-400">(boş)</span>}
        </button>
      )}

      {onConvert && (note.text || "").trim() && (
        <button
          type="button"
          onClick={onConvert}
          data-testid={`quick-note-convert-${note.id}`}
          className="hidden shrink-0 rounded p-1 text-zinc-400 hover:bg-amber-50 hover:text-amber-600 group-hover:block"
          aria-label="Göreve dönüştür"
          title="Göreve dönüştür"
        >
          <ListTodo className="h-4 w-4" strokeWidth={1.75} />
        </button>
      )}

      {note.done && (
        <button
          type="button"
          onClick={onDelete}
          data-testid={`quick-note-delete-${note.id}`}
          className="shrink-0 rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
          aria-label="Sil"
          title="Sil"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}
